/**
 * Hard-coded promo modal campaigns. Copy and IDs follow the convenzioni pattern:
 * user-facing strings live in src/i18n/*.json under the nested `promoModal` object
 * (see expandMessagesForIntl); message ids here stay dot-notation e.g. promoModal.demo.title.
 *
 * @typedef {Object} PromoModalBackgroundColor
 * @property {'color'} type
 * @property {string} value CSS color
 *
 * @typedef {Object} PromoModalBackgroundImage
 * @property {'image'} type
 * @property {string} src public URL or bundled asset URL
 * @property {string} [altMessageId] react-intl id for alt text
 * @property {number} [width] CDN resize width (defaults to modal-friendly 560)
 * @property {number} [height] CDN resize height (defaults to modal-friendly 800)
 * @property {number} [intrinsicWidth] source image width for modal aspect ratio (e.g. 1080)
 * @property {number} [intrinsicHeight] source image height for modal aspect ratio (e.g. 1920)
 * @property {string} [position] CSS object-position (default center)
 * @property {boolean} [optimize] set false to skip CDN resize and let CSS object-fit crop
 *
 * Set intrinsicWidth/intrinsicHeight to the real asset size so the hero modal matches
 * the image proportion (e.g. 1080×1920 → 9:16). Keep key art out of the bottom ~35%.
 *
 * @typedef {Object} PromoModalCta
 * @property {'dismiss' | 'navigate' | 'add_to_cart' | 'open_offcanvas'} action
 * @property {string} [href] for navigate (in-app path, e.g. /checkout)
 * @property {string} [offcanvasId] UIkit offcanvas selector for open_offcanvas (default #offcanvas-login-dx)
 * @property {string} [sku] simple product SKU for add_to_cart
 * @property {number} [quantity] quantity to add for add_to_cart (defaults to 1)
 * @property {boolean} [needStock] when true, the modal appears only if sku is in the filtered menu
 * @property {string} labelMessageId react-intl id
 * @property {string} [backgroundColor] CSS color for button
 * @property {string} [textColor] CSS color for button label
 *
 * @typedef {Object} PromoModalCampaign
 * @property {string} id stable id for storage keys
 * @property {boolean} [enabled] default true; set false to keep config in repo without showing
 * @property {number} [priority] lower runs first when multiple match
 * @property {'hero' | 'compact'} template
 * @property {PromoModalBackgroundColor | PromoModalBackgroundImage} background
 * @property {string} [contentBackgroundColor] CSS color for the readable text panel
 * @property {boolean} [ctaOnly] hide the content card and show a floating CTA on the image
 * @property {string} [titleMessageId]
 * @property {string} [bodyMessageId]
 * @property {PromoModalCta} primaryCta
 * @property {string} [dismissMessageId] secondary close link; defaults to generic key in host
 * @property {boolean} [showDismiss] show the secondary dismiss link (default true)
 * @property {number} [openDelayMs] delay before opening once matched
 * @property {'session' | 'forever' | 'none'} [dismissPersistence] where to remember dismiss / successful CTA
 * @property {{ start?: string, end?: string }} [dateRange] local YYYY-MM-DD bounds, inclusive
 * @property {number[]} [daysOfWeek] 0=Sun … 6=Sat (Date.getDay)
 * @property {{ start: string, end: string }} [hoursLocal] "HH:mm" in visitor local time
 * @property {'exact' | 'prefix'} [pathMatchMode]
 * @property {string[]} [pathPatterns] pathname must match one pattern
 * @property {string[]} [storeCodes] require checkout store cps in list
 * @property {number[]} [storeLsIds] require checkout store ls_store_id in list
 * @property {string[]} [excludeStoreCodes] hide when checkout store cps is in list
 * @property {number[]} [excludeStoreLsIds] hide when checkout store ls_store_id is in list
 * @property {string[]} [excludeStoreViewOrders] hide when store.storeViewOrder is in list (e.g. miscusi_ch_sv)
 * @property {string[]} [locales] visitor UI language must be one of these (it, en, fr, de, es)
 * @property {boolean} [guestsOnly] show only when the visitor is not signed in
 *
 * @typedef {Object} PromoModalMatchContext
 * @property {Date} now
 * @property {string} pathname
 * @property {string|undefined|null} language visitor UI language from LocaleProvider (it, en, …)
 * @property {string|undefined|null} storeCode
 * @property {number|string|undefined|null} storeLsId
 * @property {string|undefined|null} storeViewOrder e.g. miscusi_ch_sv for Swiss restaurants
 * @property {string|undefined|null} siteStoreViewCode env.store_view_code for the current site
 * @property {boolean|undefined|null} isSignedIn whether the visitor is logged in
 * @property {string[]} availableSkus filtered menu SKUs currently available for ordering
 */

/** @type {PromoModalCampaign[]} */
export const promoModalCampaigns = [{
  id: "demo_offer",
  enabled: false,
  priority: 10,
  template: "hero",
  background: {
    type: "color",
    value: "#556D67"
  },
  titleMessageId: "promoModal.demo.title",
  bodyMessageId: "promoModal.demo.body",
  primaryCta: {
    action: "dismiss",
    labelMessageId: "promoModal.demo.cta",
    backgroundColor: "#ffffff",
    textColor: "#2b2b2b"
  },
  dismissMessageId: "promoModal.demo.dismiss",
  openDelayMs: 900,
  dismissPersistence: "none",
  pathMatchMode: "exact",
  pathPatterns: ["/"]
}, {
  id: "nerano_food_waste_i020",
  enabled: false,
  priority: 1,
  template: "hero",
  background: {
    type: "image",
    src: "https://images.miscusi.com/1778886330313-Two-plates-of-pasta-topped-with-herbs-are-served-on-a-teal-table-with-bread-wine-and-checkered-napkins.png",
    altMessageId: "promoModal.neranoWaste.imageAlt"
  },
  contentBackgroundColor: "rgba(24, 42, 38, 0.82)",
  titleMessageId: "promoModal.neranoWaste.title",
  bodyMessageId: "promoModal.neranoWaste.body",
  primaryCta: {
    action: "add_to_cart",
    sku: "003185",
    quantity: 2,
    needStock: true,
    labelMessageId: "promoModal.neranoWaste.cta",
    backgroundColor: "#ffffff",
    textColor: "#2b2b2b"
  },
  dismissMessageId: "promoModal.neranoWaste.dismiss",
  openDelayMs: 8000,
  dismissPersistence: "none",
  pathMatchMode: "exact",
  pathPatterns: ["/"],
  storeCodes: ["mZgsNJJ3vZ"]
}, {
  id: "belin_loyalty_offer",
  enabled: false,
  priority: 2,
  template: "hero",
  background: {
    type: "image",
    src: "https://images.miscusi.com/1784109723715-Belin_2_nocta-_1_-webp.webp",
    altMessageId: "promoModal.belinOffer.imageAlt",
    intrinsicWidth: 1080,
    intrinsicHeight: 1920,
    optimize: false,
    position: "center top"
  },
  ctaOnly: true,
  primaryCta: {
    action: "open_offcanvas",
    offcanvasId: "#offcanvas-login-dx",
    labelMessageId: "promoModal.belinOffer.cta",
    backgroundColor: "#546d67",
    textColor: "#ffffff"
  },
  showDismiss: false,
  openDelayMs: 4500,
  dismissPersistence: "session",
  pathMatchMode: "exact",
  pathPatterns: ["/"],
  locales: ["it"],
  excludeStoreViewOrders: ["miscusi_ch_sv"],
  guestsOnly: true
}];
export default promoModalCampaigns;